import React, { useMemo } from 'react'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import LineKeySkeletonIcon from 'LuxKit/Icons/line/LineKeySkeletonIcon'
import { useIntl } from 'react-intl'

interface Props {
  offer: App.CarHireOffer
  onViewInclusions: () => void
}

function CarHireOfferTileInclusions(props: Props) {
  const { offer, onViewInclusions } = props
  const intl = useIntl()

  const unlimitedMileageLabel = intl.formatMessage({
    id: 'J71byfgXFEBzaOBf8si2',
    defaultMessage: 'Unlimited mileage',
    description: 'Inclusion label indicating unlimited mileage for car hire offer',
  })

  const limitedMileageLabel = intl.formatMessage(
    {
      id: '1tS12Nnl792Dwe2axBIp',
      defaultMessage: '{quantity}{unit}/{period} rental mileage limit',
      description: 'Inclusion label showing rental mileage limit for car hire offer, e.g. 300km/day rental mileage limit',
    },
    {
      quantity: offer.mileage.quantity,
      unit: offer.mileage.unit,
      period: offer.mileage.period,
    },
  )

  const { inclusionsList, totalInclusions } = useMemo(() => {
    const inclusions = [...offer.copy.inclusions]

    inclusions.sort((inclusion) => {
      if (inclusion.toLowerCase().includes('additional driver')) return -2
      if (inclusion.toLowerCase().includes('fee')) return -1
      return 0
    })

    inclusions.unshift(offer.mileage.unlimited ? unlimitedMileageLabel : limitedMileageLabel)

    return {
      inclusionsList: inclusions.slice(0, 3),
      totalInclusions: inclusions.length,
    }
  }, [offer, unlimitedMileageLabel, limitedMileageLabel])

  const moreInclusionsLabel = intl.formatMessage(
    {
      id: 'X0w5ORU-1rZlab-A6hwF',
      defaultMessage: '+{count} more',
      description: 'Link text showing number of additional car hire inclusions not displayed',
    },
    { count: totalInclusions - 3 },
  )

  const viewAllDetailsLabel = intl.formatMessage({
    id: 'aNHAqPOISpUt0tknry4E',
    defaultMessage: 'View all details',
    description: 'Link text to view all car hire offer inclusions and details',
  })

  return <VerticalSpacer gap={4}>
    {inclusionsList.length > 0 && <>
      {inclusionsList.map((inclusion, index) =>
        <Group
          direction="horizontal"
          gap={4}
          verticalAlign="center"
          key={inclusion}
        >
          {index === 0 && <LineKeySkeletonIcon colour="highlight-secondary" />}
          {index !== 0 && <LineCheckIcon />}
          <BodyText variant="medium" colour={index === 0 ? 'highlight-secondary' : 'neutral-two'}>{inclusion}</BodyText>
        </Group>,
      )}
    </>}
    <TextLink size="medium" onClick={onViewInclusions}>
      {totalInclusions > 3 ? moreInclusionsLabel : viewAllDetailsLabel}
    </TextLink>
  </VerticalSpacer>
}

export default CarHireOfferTileInclusions
